import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent } from './ui/card.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select.jsx';
import { Input } from './ui/input.jsx';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import axios from 'axios';
import { toast } from 'sonner';

const STAGES = [
  { key: 'nuevo_usuario', label: 'Nuevo Usuario', color: '#64748b', bg: '#f1f5f9', border: '#cbd5e1', icon: 'fas fa-user-plus' },
  { key: 'registro_iniciado', label: 'Registro Iniciado', color: '#f59e0b', bg: '#fffbeb', border: '#fcd34d', icon: 'fas fa-pen-to-square' },
  { key: 'esperando_pago', label: 'Esperando Pago', color: '#f97316', bg: '#fff7ed', border: '#fdba74', icon: 'fas fa-clock' },
  { key: 'comprobante_subido', label: 'Comprobante Subido', color: '#8b5cf6', bg: '#f5f3ff', border: '#c4b5fd', icon: 'fas fa-file-upload' },
  { key: 'pago_aprobado', label: 'Pago Aprobado', color: '#10b981', bg: '#ecfdf5', border: '#6ee7b7', icon: 'fas fa-check-circle' },
  { key: 'registrado_clases', label: 'Registrado a Clases', color: '#0ea5e9', bg: '#f0f9ff', border: '#7dd3fc', icon: 'fas fa-graduation-cap' },
];

const STAGE_LABELS = Object.fromEntries(STAGES.map(s => [s.key, s.label]));

function StudentCard({ student, index }) {
  const initials = (student.nombre || '').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const dateStr = student.fecha_registro ? new Date(student.fecha_registro).toLocaleDateString('es-UY', { day: '2-digit', month: 'short' }) : '';

  return (
    <Draggable draggableId={student.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          data-testid={`student-card-${student.id}`}
          className={`bg-white rounded-lg p-3 border transition-all duration-200 ${
            snapshot.isDragging
              ? 'shadow-lg border-blue-400 ring-2 ring-blue-200 rotate-1 scale-105'
              : 'shadow-sm border-gray-100 hover:shadow-md'
          }`}
          style={{
            ...provided.draggableProps.style,
            cursor: 'grab',
          }}
        >
          <div className="flex items-center gap-2.5 mb-1.5">
            <i className="fas fa-grip-vertical text-[10px] text-gray-300 mr-[-4px]"></i>
            {student.foto_perfil ? (
              <img src={student.foto_perfil} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center text-xs font-semibold text-slate-600 flex-shrink-0">
                {initials}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 truncate leading-tight">{student.nombre}</p>
              <p className="text-xs text-gray-500 truncate">{student.email}</p>
            </div>
            {student.manual_override && (
              <span title={student.manual_override === true ? "Posición manual" : "Automático"} className="text-[10px] text-amber-500">
                <i className={student.movido_por === 'sistema_automatico' ? 'fas fa-bolt text-blue-400' : 'fas fa-hand-point-right'}></i>
              </span>
            )}
          </div>
          <div className="flex items-center justify-between mt-1 flex-wrap gap-1">
            {student.semestre_nombre && (
              <span className="text-[10px] font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">{student.semestre_nombre}</span>
            )}
            {student.monto > 0 && (
              <span className="text-[10px] font-medium text-green-700">{student.moneda} {student.monto}</span>
            )}
            {student.num_clases > 0 && (
              <span className="text-[10px] font-medium text-indigo-600">{student.num_clases} clase{student.num_clases > 1 ? 's' : ''}</span>
            )}
            {dateStr && <span className="text-[10px] text-gray-400">{dateStr}</span>}
          </div>
        </div>
      )}
    </Draggable>
  );
}

function StageColumn({ stage, students, totalStudents }) {
  const pct = totalStudents > 0 ? Math.round((students.length / totalStudents) * 100) : 0;

  return (
    <div data-testid={`stage-column-${stage.key}`} className="flex flex-col min-w-[220px] max-w-[280px] flex-1">
      <div className="rounded-t-xl px-3 py-2.5 border-b-2" style={{ background: stage.bg, borderColor: stage.color }}>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <i className={`${stage.icon} text-sm`} style={{ color: stage.color }}></i>
            <span className="text-xs font-bold uppercase tracking-wide" style={{ color: stage.color }}>{stage.label}</span>
          </div>
          <span className="text-lg font-black" style={{ color: stage.color }}>{students.length}</span>
        </div>
        <div className="w-full h-1 rounded-full bg-white/60">
          <div className="h-1 rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: stage.color }}></div>
        </div>
        <span className="text-[10px] text-gray-500 mt-0.5 block">{pct}% del total</span>
      </div>

      <Droppable droppableId={stage.key}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`flex-1 overflow-y-auto p-2 space-y-2 border-x rounded-b-xl transition-colors duration-200 ${
              snapshot.isDraggingOver ? 'ring-2 ring-inset' : ''
            }`}
            style={{
              borderColor: stage.border,
              background: snapshot.isDraggingOver ? stage.bg : (students.length > 0 ? '#fafafa' : '#f8f8f8'),
              ringColor: snapshot.isDraggingOver ? stage.color : 'transparent',
              maxHeight: 'calc(100vh - 300px)',
              minHeight: '180px',
            }}
          >
            {students.length === 0 && !snapshot.isDraggingOver && (
              <div className="flex flex-col items-center justify-center h-24 text-gray-400">
                <i className={`${stage.icon} text-2xl mb-1 opacity-30`}></i>
                <span className="text-xs">Arrastra aquí</span>
              </div>
            )}
            {students.map((s, i) => (
              <StudentCard key={s.id} student={s} index={i} />
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
}

const PipelineRegistro = () => {
  const { user, API } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [pipeline, setPipeline] = useState(null);
  const [totals, setTotals] = useState({});
  const [semestres, setSemestres] = useState([]);
  const [selectedSemestre, setSelectedSemestre] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [moving, setMoving] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchPipeline = useCallback(async (semestreId) => {
    try {
      setLoading(true);
      const url = semestreId
        ? `${API}/admin/pipeline-registro?semestre_id=${semestreId}`
        : `${API}/admin/pipeline-registro`;
      const res = await axios.get(url, { headers });
      if (res.data.success) {
        setPipeline(res.data.pipeline);
        setTotals(res.data.totals);
        setSemestres(res.data.semestres || []);
        if (!semestreId && res.data.semestre_seleccionado) {
          setSelectedSemestre(res.data.semestre_seleccionado);
        }
      }
    } catch (err) {
      toast.error('Error al cargar pipeline de registro');
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { fetchPipeline(selectedSemestre); }, [selectedSemestre, fetchPipeline]);

  const onDragEnd = useCallback(async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination || (source.droppableId === destination.droppableId && source.index === destination.index)) return;

    // Optimistic update
    const prev = { ...pipeline };
    const sourceList = [...(pipeline[source.droppableId] || [])];
    const destList = source.droppableId === destination.droppableId ? sourceList : [...(pipeline[destination.droppableId] || [])];
    const [moved] = sourceList.splice(source.index, 1);
    moved.manual_override = true;
    destList.splice(destination.index, 0, moved);

    const updated = { ...pipeline, [source.droppableId]: sourceList };
    if (source.droppableId !== destination.droppableId) updated[destination.droppableId] = destList;
    setPipeline(updated);

    // Recalc totals
    const newTotals = {};
    let total = 0;
    for (const [k, v] of Object.entries(updated)) { newTotals[k] = v.length; total += v.length; }
    newTotals.total_estudiantes = total;
    setTotals(newTotals);

    // Persist to backend
    if (source.droppableId !== destination.droppableId) {
      setMoving(true);
      try {
        await axios.put(`${API}/admin/pipeline-registro/mover`, {
          estudiante_id: draggableId,
          etapa: destination.droppableId,
          semestre_id: selectedSemestre || ''
        }, { headers });
        toast.success(`${moved.nombre} movido a "${STAGE_LABELS[destination.droppableId]}"`);
      } catch (err) {
        toast.error('Error al mover estudiante');
        setPipeline(prev);
      } finally {
        setMoving(false);
      }
    }
  }, [pipeline, API, selectedSemestre, headers]);

  // Filter
  const filteredPipeline = useMemo(() => {
    if (!pipeline) return null;
    if (!searchTerm.trim()) return pipeline;
    const term = searchTerm.toLowerCase();
    const f = {};
    for (const [k, students] of Object.entries(pipeline)) {
      f[k] = students.filter(s => (s.nombre || '').toLowerCase().includes(term) || (s.email || '').toLowerCase().includes(term));
    }
    return f;
  }, [pipeline, searchTerm]);

  const filteredTotals = useMemo(() => {
    if (!filteredPipeline) return {};
    const t = {};
    let total = 0;
    for (const [k, v] of Object.entries(filteredPipeline)) { t[k] = v.length; total += v.length; }
    t.total_estudiantes = total;
    return t;
  }, [filteredPipeline]);

  if (user?.rol !== 'admin') {
    return <div className="p-8 text-center text-gray-500">Acceso restringido a administradores</div>;
  }

  return (
    <div data-testid="pipeline-registro-page" className="p-4 md:p-6 max-w-full">
      <div className="mb-5">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold text-gray-900">Pipeline de Registro</h1>
          {moving && <span className="text-xs text-blue-500 animate-pulse"><i className="fas fa-spinner animate-spin mr-1"></i>Guardando...</span>}
        </div>
        <p className="text-sm text-gray-500">Arrastra las tarjetas de estudiantes entre columnas para actualizar su estado</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="w-full sm:w-64">
          <Select value={selectedSemestre} onValueChange={v => setSelectedSemestre(v)}>
            <SelectTrigger data-testid="semestre-filter">
              <SelectValue placeholder="Filtrar por semestre" />
            </SelectTrigger>
            <SelectContent>
              {semestres.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-full sm:w-72 relative">
          <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
          <Input
            data-testid="search-students"
            placeholder="Buscar estudiante..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <button
          data-testid="refresh-pipeline"
          onClick={() => fetchPipeline(selectedSemestre)}
          className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-1.5"
        >
          <i className={`fas fa-sync-alt ${loading ? 'animate-spin' : ''}`}></i>
          Actualizar
        </button>
      </div>

      {totals.total_estudiantes !== undefined && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2 mb-5">
          <Card className="border-0 shadow-sm bg-gradient-to-br from-gray-50 to-gray-100">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-black text-gray-800">{totals.total_estudiantes}</p>
              <p className="text-[10px] uppercase tracking-wide font-semibold text-gray-500">Total</p>
            </CardContent>
          </Card>
          {STAGES.map(stage => (
            <Card key={stage.key} className="border-0 shadow-sm" style={{ background: stage.bg }}>
              <CardContent className="p-3 text-center">
                <p className="text-2xl font-black" style={{ color: stage.color }}>{filteredTotals[stage.key] || 0}</p>
                <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: stage.color }}>{stage.label.split(' ')[0]}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <i className="fas fa-spinner animate-spin text-2xl text-blue-500 mr-3"></i>
          <span className="text-gray-500">Cargando pipeline...</span>
        </div>
      ) : filteredPipeline ? (
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="flex gap-3 overflow-x-auto pb-4">
            {STAGES.map(stage => (
              <StageColumn
                key={stage.key}
                stage={stage}
                students={filteredPipeline[stage.key] || []}
                totalStudents={filteredTotals.total_estudiantes || 1}
              />
            ))}
          </div>
        </DragDropContext>
      ) : (
        <div className="text-center text-gray-500 py-12">No se encontraron datos</div>
      )}
    </div>
  );
};

export default PipelineRegistro;
